// The weekly intro card on To Do, for customers enrolled in the intro program
// only: this week's leads from the customer's own LinkedIn network, each
// answered with Yes (Artemis Helper sends the intro from their LinkedIn, in one
// conversation with the lead and the Artemis staff member) or Skip.
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import {
	type IntroPace,
	updateMyIntroEnrolment,
} from "../../api/intro-program/intro-enrolment";
import {
	fetchIntroWeek,
	type IntroLead,
	type IntroWeek,
	type IntroWeekResponse,
} from "../../api/intro-program/intro-program";
import { AttentionBadge } from "./AttentionBadge";
import { BaseTodoBox } from "./BaseTodoBox";
import { IntroLeadRow } from "./intro-lead-row";
import { setClassNamesByCount } from "./utils";

/** While Artemis Helper has an intro on its way, the card asks this often how it went. */
const SENDING_POLL_MS = 15_000;
const TITLE = "Introduce Artemis";

const errorText = (err: unknown): string =>
	err instanceof Error ? err.message : String(err);

const PaceControls = ({
	week,
	busy,
	onChange,
}: {
	week: IntroWeek;
	busy: boolean;
	onChange: (patch: { pace?: IntroPace; paused?: boolean }) => void;
}) => (
	<Stack
		direction="row"
		spacing={1.5}
		sx={{ alignItems: "center", justifyContent: { lg: "flex-end" } }}
		data-component="intro-pace-controls"
	>
		<Typography variant="body2" color="textSecondary">
			Per week
		</Typography>
		<ToggleButtonGroup
			data-component="intro-pace"
			size="small"
			exclusive
			value={week.pace}
			disabled={week.paused || busy}
			onChange={(_, pace: IntroPace | null) => pace && onChange({ pace })}
		>
			{([1, 2, 3] as const).map((pace) => (
				<ToggleButton
					key={pace}
					value={pace}
					data-component={`intro-pace-${pace}`}
					sx={{ px: 1.5 }}
				>
					{pace}
				</ToggleButton>
			))}
		</ToggleButtonGroup>
		<Button
			data-component="intro-pause"
			size="small"
			variant="outlined"
			disabled={busy}
			startIcon={week.paused ? <PlayArrowIcon /> : <PauseIcon />}
			onClick={() => onChange({ paused: !week.paused })}
		>
			{week.paused ? "Resume" : "Pause"}
		</Button>
	</Stack>
);

const StatusLine = ({
	dataComponent,
	children,
}: {
	dataComponent: string;
	children: ReactNode;
}) => (
	<Stack
		direction="row"
		spacing={1.5}
		sx={{ alignItems: "center", py: 2, borderTop: 1, borderColor: "divider" }}
		data-component={dataComponent}
	>
		<CheckCircleOutlineIcon color="success" />
		<Box>{children}</Box>
	</Stack>
);

const DoneForTheWeek = ({ leads }: { leads: IntroLead[] }) => {
	const skipped = leads.filter((lead) => lead.status === "skipped").length;
	const sent = leads.length - skipped;
	return (
		<StatusLine dataComponent="intro-done">
			<Typography variant="subtitle1">All done for this week</Typography>
			<Typography variant="body2" color="textSecondary">
				{sent} {sent === 1 ? "intro" : "intros"} sent, {skipped} skipped. Your
				next leads arrive on Monday.
			</Typography>
		</StatusLine>
	);
};

const isWaiting = (lead: IntroLead): boolean =>
	lead.status === "pending" && lead.send_state !== "queued";

const WeekBody = ({
	week,
	onLeadChange,
}: {
	week: IntroWeek;
	onLeadChange: (lead: IntroLead) => void;
}) => {
	// Paused stops new leads; this week's leads, and how the sent ones are
	// doing, stay in view.
	const paused = week.paused ? (
		<Typography
			variant="body2"
			color="textSecondary"
			sx={{ pt: 2, pb: 1, borderTop: 1, borderColor: "divider" }}
			data-component="intro-paused"
		>
			Paused. No new leads until you resume.
		</Typography>
	) : null;
	if (week.leads.length === 0)
		return (
			paused ?? (
				<StatusLine dataComponent="intro-joined-empty">
					<Typography variant="subtitle1">
						Thanks! Your first suggestions arrive on Monday.
					</Typography>
				</StatusLine>
			)
		);
	const waiting = week.leads.some(isWaiting);
	const answered = week.leads.every((lead) => lead.status !== "pending");
	return (
		<>
			{paused}
			{week.template_error !== null &&
			week.leads.some((lead) => isWaiting(lead) && !lead.can_send) ? (
				<Alert
					severity="error"
					sx={{ mb: 1 }}
					data-component="intro-template-error"
				>
					{week.template_error}
				</Alert>
			) : null}
			{waiting ? (
				<Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
					People you have talked with, picked by the Artemis team. Yes sends
					the intro from your LinkedIn right away, in one conversation with
					the lead and {week.staff_first_name}. Skip passes them over for
					good.
				</Typography>
			) : null}
			{week.leads
				.filter((lead) => lead.status !== "skipped")
				.map((lead) => (
					<IntroLeadRow
						key={lead.id}
						lead={lead}
						staffFirstName={week.staff_first_name}
						onChange={onLeadChange}
					/>
				))}
			{answered && !week.paused ? <DoneForTheWeek leads={week.leads} /> : null}
		</>
	);
};

export const IntroProgramCard = () => {
	// undefined while the first load runs: nothing is drawn, since most users
	// are not enrolled and must not see a card flash by.
	const [week, setWeek] = useState<IntroWeekResponse>();
	const [loadError, setLoadError] = useState("");
	const [settingsBusy, setSettingsBusy] = useState(false);
	const [settingsError, setSettingsError] = useState("");

	const load = useCallback(
		() =>
			fetchIntroWeek()
				.then((loaded) => {
					setWeek(loaded);
					setLoadError("");
				})
				.catch((err) => setLoadError(errorText(err))),
		[]
	);

	useEffect(() => {
		void load();
	}, [load]);

	const sending =
		week?.enrolled === true &&
		week.leads.some((lead) => lead.send_state === "queued");
	useEffect(() => {
		if (!sending) return;
		const timer = window.setInterval(() => void load(), SENDING_POLL_MS);
		return () => window.clearInterval(timer);
	}, [sending, load]);

	if (loadError !== "" && week === undefined)
		return (
			<BaseTodoBox
				dataComponent="intro-program-card"
				title={TITLE}
				subRow={
					<Alert severity="error" data-component="intro-load-error">
						Could not load your intro suggestions: {loadError}
					</Alert>
				}
			/>
		);
	if (week === undefined || !week.enrolled) return null;

	const count = week.paused ? 0 : week.leads.filter(isWaiting).length;

	const changeSettings = async (patch: { pace?: IntroPace; paused?: boolean }) => {
		setSettingsBusy(true);
		setSettingsError("");
		try {
			await updateMyIntroEnrolment(patch);
			await load();
		} catch (err) {
			setSettingsError(errorText(err));
		} finally {
			setSettingsBusy(false);
		}
	};

	const replaceLead = (lead: IntroLead) =>
		setWeek({
			...week,
			leads: week.leads.map((one) => (one.id === lead.id ? lead : one)),
		});

	return (
		<BaseTodoBox
			dataComponent="intro-program-card"
			className={setClassNamesByCount(count)}
			title={<AttentionBadge count={count} title={TITLE} readOnly url="" />}
			description={
				<PaceControls
					week={week}
					busy={settingsBusy}
					onChange={changeSettings}
				/>
			}
			hint={{
				heading: "Introduce Artemis to your network",
				body: `The Artemis team picks people you have talked with who could use Artemis. Yes sends the intro from your LinkedIn in one conversation with the lead and ${week.staff_first_name}. Skip passes them over for good.`,
			}}
			subRow={
				<Box sx={{ mt: 2 }}>
					{settingsError !== "" || loadError !== "" ? (
						<Alert
							severity="error"
							sx={{ mb: 1 }}
							data-component="intro-card-error"
						>
							{settingsError !== "" ? settingsError : loadError}
						</Alert>
					) : null}
					<WeekBody week={week} onLeadChange={replaceLead} />
				</Box>
			}
		/>
	);
};
